// Research Mode — event logger with batched flush
// Captures: game_start, attempt_correct, attempt_wrong, game_win, game_exit
import { API } from "@/lib/api";

let context = {
  childId: null,
  sessionId: null,
  layer: null,
  gameKey: null,
  enabled: false,
};

let queue = [];
let flushTimer = null;
const FLUSH_INTERVAL_MS = 2500;
const MAX_QUEUE = 50;

const genSessionId = () =>
  `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export const setResearchEnabled = (on) => {
  context.enabled = !!on;
  if (on && !context.sessionId) context.sessionId = genSessionId();
};

export const setChildContext = (childId) => {
  if (context.childId !== childId) {
    flush();
    context.childId = childId;
    context.sessionId = genSessionId();
  }
};

export const setGameContext = (layer, gameKey) => {
  context.layer = layer;
  context.gameKey = gameKey;
};

export const clearGameContext = () => {
  context.layer = null;
  context.gameKey = null;
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
};

export const flush = async () => {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  try {
    await fetch(`${API}/research/events/batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(batch),
    });
  } catch (err) {
    console.warn("Research flush failed, re-queueing:", err);
    // Put back in queue (front)
    queue = [...batch, ...queue].slice(-200);
  }
};

const enqueue = (event_type, metadata = {}) => {
  if (!context.enabled || !context.childId || !context.layer || !context.gameKey) return;
  queue.push({
    child_id: context.childId,
    session_id: context.sessionId,
    layer: context.layer,
    game_key: context.gameKey,
    event_type,
    metadata,
    client_timestamp: new Date().toISOString(),
  });
  if (queue.length >= MAX_QUEUE) {
    flush();
  } else {
    scheduleFlush();
  }
};

export const logGameStart = (meta) => enqueue("game_start", meta || {});
export const logGameWin = (meta) => enqueue("game_win", meta || {});
export const logGameExit = (meta) => enqueue("game_exit", meta || {});
export const logAttempt = (correct, meta) =>
  enqueue(correct ? "attempt_correct" : "attempt_wrong", meta || {});

// Flush on page hide
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    if (queue.length > 0) {
      try {
        navigator.sendBeacon &&
          navigator.sendBeacon(
            `${API}/research/events/batch`,
            new Blob([JSON.stringify(queue)], { type: "application/json" })
          );
        queue = [];
      } catch (err) {
        console.warn("sendBeacon flush on unload failed:", err);
      }
    }
  });
}
